import { type ReadonlyVec2, vec2 } from 'gl-matrix';

import { vec2extended } from '@canvas/math/vec2';

import {
  convertCubicBezierToGlobalCoordinateSystem,
  convertCubicBezierPointsToLines,
  getPathSegmentsFromPoints,
} from './path.ts';
import { convertArcToLines, getPointAlongArc } from './segments/arc.ts';
import { getCapLinesForPath } from './segments/cap.ts';
import { type PathSegment, type PathStyleForLegacyCaps } from './types.ts';

/**
 * Walks the segments composing a path, calling `onSegment` at the end of each
 * segment.
 *
 * @param start the starting point
 * @param segments the array of path segments to walk
 * @param onSegment a callback called for each computed segment, which is passed
 * the start and end co-ords of the segment as well as the segment itself.
 * Optionally returning false from `onSegment` will cancel the walk.
 */
export const walkPathSegments = <T extends PathSegment>(
  start: ReadonlyVec2,
  segments: T[],
  onSegment: (
    segmentStart: vec2,
    segmentEnd: vec2,
    segment: T,
    i: number,
  ) => boolean | void,
) => {
  const cursor: vec2 = vec2.clone(start);

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];

    // mark start of new segment
    const segmentStart = vec2.clone(cursor);

    // move cursor to end of next segment
    switch (segment.type) {
      case 'line':
        vec2.add(
          cursor,
          segmentStart,
          vec2extended.fromPolar(segment.angle, segment.length),
        );
        break;
      case 'arc': {
        vec2.copy(cursor, getPointAlongArc(segmentStart, segment, segment.endAngle));
        break;
      }
      case 'cubic-bezier': {
        vec2.add(cursor, cursor, segment.end);
        break;
      }
    }

    // mark end of new segment
    const segmentEnd = vec2.clone(cursor);

    const result = onSegment(segmentStart, segmentEnd, segment, i);
    if (result === false) {
      return;
    }
  }
};

/**
 * Walks the segments composing a path, calling `onSegment` at the end of each
 * segment, and additionally calling `onSegment` for lines produce by end caps
 *
 * @param start the starting point
 * @param segments the array of path segments to walk
 * @param pathStyleConfig if not undefined, this function will also output segments of end caps
 * @param onSegment a callback called for each computed segment, which is passed
 * the start and end co-ords of the segment as well as the segment itself.
 * Optionally returning false from `onSegment` will cancel the walk.
 *
 * @deprecated
 * all legacy caps code will be removed after diagrammingArrowheads feature is rolled out
 * use walkPathSegments instead
 */
export const walkPathSegmentsWithEndCaps = (
  start: ReadonlyVec2,
  segments: PathSegment[],
  pathStyleConfig: PathStyleForLegacyCaps,
  onSegment: (
    segmentStart: vec2,
    segmentEnd: vec2,
    segment: PathSegment,
  ) => boolean | void,
) => {
  // first, walk path segments
  let cursor: vec2 = vec2.clone(start);
  let stopTraversal = false;
  walkPathSegments(start, segments, (start, end, segment) => {
    cursor = end;
    const result = onSegment(start, end, segment);
    if (result === false) {
      stopTraversal = true;
    }
    return result;
  });

  // if walk was stopped, don't walk end caps
  if (stopTraversal) {
    return;
  }

  // get lines for end caps according to style config
  const decorationLines = getCapLinesForPath(
    start,
    vec2.clone(cursor),
    segments,
    pathStyleConfig,
  );

  // call onSegment for each
  for (let i = 0; i < decorationLines.length; i++) {
    const currentLine = decorationLines[i];
    const result = onSegment(
      currentLine.start,
      currentLine.end,
      getPathSegmentsFromPoints(currentLine.start, currentLine.end)[0],
    );

    if (result === false) {
      break;
    }
  }
};

/**
 * Walks the segments composing a path, converting any arcs to a set of lines.
 *
 * @param start the start of the arc
 * @param segments the path segments to traverse
 * @param onSegment the callback to call for each line to produce
 * @param options.numLines the number of lines to split the arc into
 * @param options.overlap should the algorithm produce an overlapping set of lines for smooth rendering purposes
 */
export const walkAndConvertPathSegmentsToLines = (
  start: ReadonlyVec2,
  segments: PathSegment[],
  onSegment: (start: vec2, end: vec2, uv: vec2) => boolean | void,
  options: { numLines?: number; overlap?: boolean } = {},
): void => {
  const cursor: vec2 = vec2.clone(start);
  let arcLength = 0;

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];

    switch (segment.type) {
      case 'line': {
        const start = vec2.clone(cursor);
        vec2.add(cursor, start, vec2extended.fromPolar(segment.angle, segment.length));

        const end = vec2.clone(cursor);
        if (
          segment.pathSegmentMetadata === undefined ||
          segment.pathSegmentMetadata.isVisible !== false
        ) {
          onSegment(start, end, vec2.fromValues(arcLength, arcLength + segment.length));
        }

        arcLength += segment.length;
        break;
      }
      case 'arc': {
        const arcAsLines = convertArcToLines(cursor, segment, options);

        for (let j = 0; j < arcAsLines.length; j++) {
          const { start, end, startArcLength, endArcLength } = arcAsLines[j];

          onSegment(
            start,
            end,
            vec2.fromValues(arcLength + startArcLength, arcLength + endArcLength),
          );
        }

        if (arcAsLines.length > 0) {
          vec2.copy(cursor, getPointAlongArc(cursor, segment, segment.endAngle));
          arcLength += Math.abs(segment.radius * (segment.endAngle - segment.startAngle));
        }

        break;
      }
      case 'cubic-bezier': {
        const bezier = convertCubicBezierToGlobalCoordinateSystem(segment, cursor);

        const cubicBezierAsLines = convertCubicBezierPointsToLines(bezier);

        const diff = vec2.create();
        let prevLength = 0;

        for (let j = 0; j < cubicBezierAsLines.length; j++) {
          const { start, end } = cubicBezierAsLines[j];

          vec2.subtract(diff, end, start);
          const currLength = prevLength + vec2.len(diff);

          if (
            segment.pathSegmentMetadata === undefined ||
            segment.pathSegmentMetadata.isVisible !== false
          ) {
            onSegment(start, end, vec2.fromValues(prevLength, currLength));
          }

          prevLength = currLength;
        }

        if (cubicBezierAsLines.length > 0) {
          vec2.copy(cursor, bezier.end);
        }
        break;
      }
    }
  }
};
